import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
    ChevronLeft,
    ChevronRight,
    Calendar as CalendarIcon,
    MapPin,
    Clock,
    X,
    ExternalLink,
    ChevronDown,
    Users,
    Hash,
    Utensils,
    RefreshCw,
    Sun,
    Moon,
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { VENUES } from '../../lib/constants';
import PaymentDetailModal from '../modals/PaymentDetailModal';
import BookingDetailModal from '../modals/BookingDetailModal';
import { bookingService } from '../../services/booking.service';
import { leadsService } from '../../services/api';
import { Dropdown, DropdownTrigger, DropdownPopover, DropdownMenu, DropdownItem } from '../ui';
import { formatDate } from '../../utils/helpers';

// ─── EventChip ────────────────────────────────────────────────────────────────
// Google Calendar-style colored pill inside each day cell
const EventChip = ({ event, onClick }) => {
    const { t } = useLanguage();
    const isDay = (event.shift || 'Day').toLowerCase() === 'day' || (event.shift || '').toLowerCase() === 'morning';
    const isTentative = event.status === 'tentative';
    
    return (
        <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onClick(event, e); }}
            title={`${event.clientName || 'Booking'} · ${isDay ? (t('dayShift') || 'Morning Shift') : (t('nightShift') || 'Evening Shift')} ${isTentative ? '(Tentative)' : ''}`}
            className={`
                w-full text-left flex items-center gap-1 px-1.5 py-[3px] rounded-md
                text-[11px] font-semibold leading-none truncate
                transition-all duration-150 hover:brightness-95 active:scale-[0.97]
                ${isTentative ? 'border border-dashed' : ''}
                ${isDay
                    ? (isTentative ? 'bg-amber-50 text-amber-600 border-amber-300' : 'bg-amber-100 text-amber-800 hover:bg-amber-200')
                    : (isTentative ? 'bg-indigo-50 text-indigo-600 border-indigo-300' : 'bg-indigo-100 text-indigo-800 hover:bg-indigo-200')
                }
            `}
        >
            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isDay ? 'bg-amber-500' : 'bg-indigo-500'} ${isTentative ? 'opacity-50' : ''}`} />
            <span className="truncate">{event.clientName || 'Booking'} {isTentative && <span className="text-[9px] font-normal opacity-70 italic ml-0.5">(T)</span>}</span>
        </button>
    );
};


// ─── CalendarCell ──────────────────────────────────────────────────────────────
// Google Calendar-style day cell
const CalendarCell = ({ day, events, isToday, isWeekend, isOtherMonth, onEventClick, isExpandedInAgenda, isWeekView }) => {
    const [expanded, setExpanded] = useState(false);

    if (!day) {
        return (
            <div className={`${isWeekView ? 'min-h-[140px]' : 'min-h-[90px]'} bg-white border-r border-b border-slate-100`} />
        );
    }

    const MAX_VISIBLE = isWeekView ? 5 : 3;
    const displayEvents = expanded || isExpandedInAgenda ? events : events.slice(0, MAX_VISIBLE);
    const hasMore = events.length > MAX_VISIBLE && !expanded && !isExpandedInAgenda;

    return (
        <div
            className={`
                ${isWeekView ? 'min-h-[140px]' : 'min-h-[90px]'}
                flex flex-col bg-white border-r border-b border-slate-100
                transition-colors duration-100
                ${isOtherMonth ? 'bg-slate-50/40' : ''}
                ${!isOtherMonth && !isWeekend ? 'hover:bg-blue-50/20' : ''}
                ${isWeekend && !isOtherMonth ? 'bg-slate-50/30' : ''}
            `}
        >
            {/* Day number row */}
            {!isWeekView && (
                <div className="flex items-center justify-between px-2 pt-1.5 pb-1">
                    <span
                        className={`
                            text-[13px] font-semibold w-7 h-7 flex items-center justify-center rounded-full
                            transition-all cursor-default select-none
                            ${isToday
                                ? 'bg-blue-600 text-white font-bold shadow-sm'
                                : isWeekend
                                    ? 'text-rose-400 hover:bg-rose-50'
                                    : isOtherMonth
                                        ? 'text-slate-300'
                                        : 'text-slate-700 hover:bg-slate-100'
                            }
                        `}
                    >
                        {day}
                    </span>
                </div>
            )}

            {/* Events */}
            <div className={`flex-1 px-1 pb-1 space-y-0.5 overflow-hidden relative ${expanded ? 'overflow-visible' : ''}`}>
                {expanded && (
                    <div
                        className="absolute inset-0 bg-white z-20 rounded-lg border border-slate-200 shadow-xl p-2 space-y-1 overflow-y-auto"
                        style={{ minHeight: 'min-content' }}
                    >
                        <div className="flex justify-between items-center mb-1.5">
                            <span className="text-[11px] font-bold text-slate-600">{events.length} bookings</span>
                            <button
                                onClick={(e) => { e.stopPropagation(); setExpanded(false); }}
                                className="p-0.5 hover:bg-slate-100 rounded-full"
                            >
                                <X className="w-3 h-3 text-slate-500" />
                            </button>
                        </div>
                        {events.map(evt => (
                            <EventChip key={evt.id} event={evt} onClick={onEventClick} />
                        ))}
                    </div>
                )}
                {!expanded && displayEvents.map(evt => (
                    <EventChip key={evt.id} event={evt} onClick={onEventClick} />
                ))}
                {hasMore && (
                    <button
                        onClick={(e) => { e.stopPropagation(); setExpanded(true); }}
                        className="w-full text-left text-[11px] text-blue-600 font-semibold hover:bg-blue-50 rounded px-1.5 py-0.5 transition-colors"
                    >
                        +{events.length - MAX_VISIBLE} more
                    </button>
                )}
            </div>
        </div>
    );
};

// --- Main View --- //
export default function CalendarView({ leads, currentUser }) {
    const { t } = useLanguage();

    const [view, setView] = useState('month'); // day | week | month | year | agenda
    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedVenue, setSelectedVenue] = useState('all');
    const [selectedShift, setSelectedShift] = useState('all');

    // Banquet bookings state
    const [banquetBookings, setBanquetBookings] = useState([]);
    const [banquetLoading, setBanquetLoading] = useState(false);
    const [banquetError, setBanquetError] = useState(null);
    // Keep previous bookings visible while new month loads (no blank flash)
    const prevBookingsRef = React.useRef([]);

    // Popup state
    const [popupEvent, setPopupEvent] = useState(null); // { event, position: {x, y} }

    // Finance Role Payment Modal State
    const [financeLeadForPayment, setFinanceLeadForPayment] = useState(null);

    // All tentative leads (fetched from dedicated endpoint, no role filtering)
    const [allTentativeLeads, setAllTentativeLeads] = useState([]);

    // Fetch all Site Visited leads for calendar (disabled for demo, using leads prop directly)
    useEffect(() => {
        // Disabled
    }, []);

    // Fetch banquet bookings whenever month/year changes (disabled for demo)
    const fetchBanquetBookings = useCallback(async (year, month) => {
        // Disabled
        setBanquetBookings([]);
    }, []);

    useEffect(() => {
        fetchBanquetBookings(currentDate.getFullYear(), currentDate.getMonth());
    }, [currentDate.getFullYear(), currentDate.getMonth(), fetchBanquetBookings]);

    // Responsive Auto-Switch to Agenda
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth < 768) {
                setView('agenda');
            }
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Navigate calendar
    const navigateDate = (dir) => {
        const tempDates = new Date(currentDate);
        if (view === 'month' || view === 'agenda') {
            tempDates.setMonth(currentDate.getMonth() + dir);
        } else if (view === 'week') {
            tempDates.setDate(currentDate.getDate() + (dir * 7));
        } else if (view === 'day') {
            tempDates.setDate(currentDate.getDate() + dir);
        } else if (view === 'year') {
            tempDates.setFullYear(currentDate.getFullYear() + dir);
        }
        setCurrentDate(tempDates);
    };

    const goToday = () => {
        setCurrentDate(new Date());
    };

    const venues = useMemo(() => {
        return VENUES.map(v => ({ id: v.id, name: v.name }));
    }, []);

    // Apply filters to local CRM leads directly
    const bookedEvents = useMemo(() => {
        const localBookings = (leads || []).filter(lead => {
            if (!['Won', 'Completed', 'Site Visited'].includes(lead.stage)) return false;
            if (!lead.eventDate) return false;
            
            if (selectedVenue !== 'all') {
                const lVenue = String(lead.venue || '').toLowerCase();
                const targetVenue = String(selectedVenue).toLowerCase();
                const venueObj = VENUES.find(v => v.id === selectedVenue);
                const venueMatch = lVenue === targetVenue ||
                    (venueObj && venueObj.name.toLowerCase() === lVenue);
                if (!venueMatch) return false;
            }

            if (selectedShift !== 'all') {
               const lShift = (lead.shift || 'Day').toLowerCase();
               const filterShift = selectedShift.toLowerCase();
               if (filterShift === 'day' || filterShift === 'morning') {
                   if (lShift !== 'day' && lShift !== 'morning') return false;
               } else if (filterShift === 'night' || filterShift === 'evening') {
                   if (lShift !== 'night' && lShift !== 'evening') return false;
               } else if (lShift !== filterShift) {
                   return false;
               }
            }
            return true;
        }).map(lead => {
            const isTentative = lead.stage === 'Site Visited';
            return {
                id: `local-${isTentative ? 'tentative' : 'confirmed'}-${lead.id}`,
                banquetId: null,
                source: 'crm',
                clientName: lead.clientName || lead.title || 'Unknown',
                eventDate: lead.eventDate ? new Date(lead.eventDate).toISOString().split('T')[0] : '',
                shift: lead.shift || 'Day',
                venue: lead.venue || 'Unknown',
                functionName: lead.eventType || (isTentative ? 'Tentative Booking' : 'Confirmed Booking'),
                status: isTentative ? 'tentative' : 'confirmed',
                crmId: lead.id,
                assignee: lead.assignee || { username: lead.manager },
            };
        });

        return localBookings.sort((a, b) => {
            // Day/Morning shift before Night/Evening shift
            const shiftA = (a.shift || 'Day').toLowerCase();
            if (shiftA === 'day' || shiftA === 'morning') return -1;
            return 1;
        });
    }, [leads, selectedVenue, selectedShift]);

    // Calendar math
    const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
    const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getDay();
    const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    const daysOfWeek = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

    const handleEventClick = (event, e) => {
        e.stopPropagation();
        const rect = e.currentTarget?.getBoundingClientRect?.() || null;
        setPopupEvent({ event, anchorRect: rect });
    };

    const handleViewLead = (event) => {
        setPopupEvent(null);
        if (currentUser?.role?.toLowerCase() === 'finance') {
            setFinanceLeadForPayment(event);
        } else if (event.source === 'banquet' && event.crmId) {
            // Navigate to linked CRM lead
            const url = new URL(window.location);
            url.searchParams.set('leadId', event.crmId);
            window.history.pushState({}, '', url);
            window.dispatchEvent(new PopStateEvent('popstate'));
        } else if (event.source !== 'banquet') {
            const url = new URL(window.location);
            url.searchParams.set('leadId', event.crmId || event.id);
            window.history.pushState({}, '', url);
            window.dispatchEvent(new PopStateEvent('popstate'));
        }
    };

    // Render logic per view
    const renderContent = () => {
        switch (view) {
            case 'year':
                const months = Array.from({ length: 12 }, (_, i) => i);
                return (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 p-6 overflow-y-auto">
                        {months.map(m => {
                            const dInM = new Date(currentDate.getFullYear(), m + 1, 0).getDate();
                            const fDay = new Date(currentDate.getFullYear(), m, 1).getDay();

                            const bks = Array(fDay).fill(null);
                            const ds = Array.from({ length: dInM }, (_, i) => i + 1);
                            const cells = [...bks, ...ds];

                            return (
                                <div
                                    key={m}
                                    onClick={() => {
                                        setCurrentDate(new Date(currentDate.getFullYear(), m, 1));
                                        setView('month');
                                    }}
                                    className="bg-white rounded-2xl p-4 border border-slate-200 cursor-pointer hover:shadow-lg hover:border-blue-200 transition-all active:scale-[0.98] group"
                                >
                                    <h4 className="font-bold text-slate-800 mb-2 group-hover:text-blue-600 transition-colors">{monthNames[m]}</h4>
                                    <div className="grid grid-cols-7 gap-1 text-[8px] font-bold text-slate-400 mb-1 text-center">
                                        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((dw, i) => (<div key={i}>{dw}</div>))}
                                    </div>
                                    <div className="grid grid-cols-7 gap-1">
                                        {cells.map((d, index) => {
                                            const hasEVs = d && bookedEvents.some(e => {
                                                const ed = new Date(e.eventDate);
                                                return ed.getDate() === d && ed.getMonth() === m && ed.getFullYear() === currentDate.getFullYear();
                                            });
                                            return (
                                                <div key={index} className="aspect-square flex justify-center items-center relative">
                                                    {d && (
                                                        <span className={`w-5 h-5 flex items-center justify-center rounded-full ${hasEVs ? 'bg-blue-100 text-blue-700 font-bold' : 'text-slate-600'}`}>{d}</span>
                                                    )}
                                                    {hasEVs && <span className="absolute bottom-[2px] w-1 h-1 bg-blue-500 rounded-full" />}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                );
            case 'week':
                const startOfWeekDate = currentDate.getDate() - currentDate.getDay();
                const weekDates = Array.from({ length: 7 }, (_, i) => {
                    const nd = new Date(currentDate.getFullYear(), currentDate.getMonth(), startOfWeekDate + i);
                    return nd;
                });
                return (
                    <div className="flex-1 flex flex-col h-full overflow-hidden">
                        {/* Week Header with day names + dates */}
                        <div className="grid grid-cols-7 border-b border-slate-200">
                            {weekDates.map((wd, i) => {
                                const isWeekToday = wd.toDateString() === new Date().toDateString();
                                const isWkEnd = wd.getDay() === 0 || wd.getDay() === 6;
                                return (
                                    <div key={i} className={`py-3 text-center border-r border-slate-100 ${isWeekToday ? 'bg-blue-50' : 'bg-slate-50/80'}`}>
                                        <p className={`text-[10px] font-bold tracking-widest ${isWkEnd ? 'text-rose-400' : 'text-slate-400'}`}>{daysOfWeek[wd.getDay()]}</p>
                                        <p className={`text-lg font-black mt-0.5 ${isWeekToday ? 'text-blue-600' : 'text-slate-800'}`}>{wd.getDate()}</p>
                                    </div>
                                );
                            })}
                        </div>
                        {/* Week Grid — cells without day numbers (header already shows them) */}
                        <div className="flex-1 grid grid-cols-7 overflow-y-auto">
                            {weekDates.map((wd, i) => {
                                const dayEvents = bookedEvents.filter(e => {
                                    const ed = new Date(e.eventDate);
                                    return ed.getDate() === wd.getDate() && ed.getMonth() === wd.getMonth() && ed.getFullYear() === wd.getFullYear();
                                });
                                return (
                                    <CalendarCell
                                        key={i}
                                        day={wd.getDate()}
                                        events={dayEvents}
                                        isToday={wd.toDateString() === new Date().toDateString()}
                                        isWeekend={wd.getDay() === 0 || wd.getDay() === 6}
                                        onEventClick={handleEventClick}
                                        isExpandedInAgenda={true}
                                        isWeekView={true}
                                    />
                                );
                            })}
                        </div>
                    </div>
                );
            case 'day':
                const dayEvs = bookedEvents.filter(e => {
                    const ed = new Date(e.eventDate);
                    return ed.getDate() === currentDate.getDate() && ed.getMonth() === currentDate.getMonth() && ed.getFullYear() === currentDate.getFullYear();
                });

                const dayShiftEvs = dayEvs.filter(e => (e.shift || 'Day').toLowerCase() === 'day');
                const nightShiftEvs = dayEvs.filter(e => (e.shift || 'Day').toLowerCase() === 'night');

                return (
                    <div className="p-4 md:p-6 overflow-y-auto flex-1 bg-gradient-to-b from-slate-50 to-white">
                        {/* Day Header */}
                        <div className="max-w-3xl mx-auto mb-6">
                            <div className="flex items-center gap-3 mb-1">
                                <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-black text-lg shadow-sm ${currentDate.toDateString() === new Date().toDateString()
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-slate-100 text-slate-700'
                                    }`}>
                                    {currentDate.getDate()}
                                </div>
                                <div>
                                    <h3 className="text-xl font-extrabold text-slate-800">
                                        {daysOfWeek[currentDate.getDay()]}, {monthNames[currentDate.getMonth()]} {currentDate.getDate()}
                                    </h3>
                                    <p className="text-xs text-slate-400 font-medium">
                                        {dayEvs.length} booking{dayEvs.length !== 1 ? 's' : ''} scheduled
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="max-w-3xl mx-auto space-y-6">
                            {dayEvs.length === 0 ? (
                                <div className="p-16 text-center bg-white rounded-2xl border border-slate-100 shadow-sm">
                                    <CalendarIcon className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                                    <p className="text-slate-500 font-medium">No bookings scheduled for this day.</p>
                                    <p className="text-slate-400 text-sm mt-1">Use the navigation arrows to browse other dates.</p>
                                </div>
                            ) : (
                                <>
                                    {/* Day Shift Section */}
                                    {dayShiftEvs.length > 0 && (
                                        <div>
                                            <div className="flex items-center gap-2 mb-3">
                                                <div className="w-2.5 h-2.5 rounded-full bg-amber-400 shadow-sm" />
                                                <h4 className="text-sm font-bold text-slate-500 uppercase tracking-wider">{t('dayShift') || 'Morning Shift'}</h4>
                                                <span className="text-xs bg-amber-50 text-amber-600 font-bold px-2 py-0.5 rounded-full">{dayShiftEvs.length}</span>
                                            </div>
                                            <div className="space-y-2">
                                                {dayShiftEvs.map(evt => (
                                                    <div key={evt.id} onClick={(e) => handleEventClick(evt, e)}
                                                        className="flex items-center gap-4 p-4 bg-white border border-slate-200 rounded-xl hover:shadow-md hover:border-amber-200 cursor-pointer transition-all active:scale-[0.98] border-l-4 border-l-amber-400">
                                                        <div className="flex-1 min-w-0">
                                                            <h4 className="font-bold text-[15px] text-slate-800 truncate">{evt.clientName || evt.title || 'Client Booking'}</h4>
                                                            <div className="flex items-center gap-3 mt-1.5">
                                                                <div className="flex items-center gap-1.5 text-slate-400 text-xs">
                                                                    <MapPin className="w-3.5 h-3.5" />
                                                                    <span>{VENUES.find(v => v.id === evt.venue)?.name || evt.venue}</span>
                                                                </div>
                                                                {evt.assignee && (
                                                                    <div className="flex items-center gap-1.5 text-slate-400 text-xs">
                                                                        <User className="w-3.5 h-3.5" />
                                                                        <span>{evt.assignee.username}</span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0" />
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Night Shift Section */}
                                    {nightShiftEvs.length > 0 && (
                                        <div>
                                            <div className="flex items-center gap-2 mb-3">
                                                <div className="w-2.5 h-2.5 rounded-full bg-indigo-500 shadow-sm" />
                                                <h4 className="text-sm font-bold text-slate-500 uppercase tracking-wider">{t('nightShift') || 'Evening Shift'}</h4>
                                                <span className="text-xs bg-indigo-50 text-indigo-600 font-bold px-2 py-0.5 rounded-full">{nightShiftEvs.length}</span>
                                            </div>
                                            <div className="space-y-2">
                                                {nightShiftEvs.map(evt => (
                                                    <div key={evt.id} onClick={(e) => handleEventClick(evt, e)}
                                                        className="flex items-center gap-4 p-4 bg-white border border-slate-200 rounded-xl hover:shadow-md hover:border-indigo-200 cursor-pointer transition-all active:scale-[0.98] border-l-4 border-l-indigo-500">
                                                        <div className="flex-1 min-w-0">
                                                            <h4 className="font-bold text-[15px] text-slate-800 truncate">{evt.clientName || evt.title || 'Client Booking'}</h4>
                                                            <div className="flex items-center gap-3 mt-1.5">
                                                                <div className="flex items-center gap-1.5 text-slate-400 text-xs">
                                                                    <MapPin className="w-3.5 h-3.5" />
                                                                    <span>{VENUES.find(v => v.id === evt.venue)?.name || evt.venue}</span>
                                                                </div>
                                                                {evt.assignee && (
                                                                    <div className="flex items-center gap-1.5 text-slate-400 text-xs">
                                                                        <User className="w-3.5 h-3.5" />
                                                                        <span>{evt.assignee.username}</span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0" />
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                );
            case 'agenda':
                const agendaEvents = bookedEvents.filter(e => {
                    const d = new Date(e.eventDate);
                    return d.getMonth() === currentDate.getMonth() && d.getFullYear() === currentDate.getFullYear();
                }).sort((a, b) => new Date(a.eventDate) - new Date(b.eventDate));

                // Group by date string to mimic Google Calendar
                const groupedAgenda = agendaEvents.reduce((acc, evt) => {
                    const d = new Date(evt.eventDate);
                    d.setHours(0, 0, 0, 0);
                    const key = d.toISOString();
                    if (!acc[key]) acc[key] = { date: d, events: [] };
                    acc[key].events.push(evt);
                    return acc;
                }, {});

                const sortedGroups = Object.values(groupedAgenda).sort((a, b) => a.date - b.date);

                return (
                    <div className="flex flex-col h-full overflow-y-auto custom-scrollbar bg-white flex-1 pt-2">
                        {sortedGroups.length === 0 && (
                            <div className="text-center text-slate-500 p-8 mt-4">
                                No upcoming bookings found from {formatDate(currentDate)}.
                            </div>
                        )}
                        <div className="max-w-5xl w-full mx-auto pb-10">
                            {sortedGroups.map((group, idx) => (
                                <div key={group.date.toISOString()} className={`flex flex-col md:flex-row gap-4 md:gap-8 px-6 py-4 ${idx !== 0 ? 'border-t border-gray-100' : ''}`}>
                                    {/* Left Column: Date & Day */}
                                    <div className="w-full md:w-28 flex-shrink-0 flex items-baseline gap-2 md:pt-1.5">
                                        <span className="text-[22px] text-slate-700 leading-none">{group.date.getDate()}</span>
                                        <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                                            {monthNames[group.date.getMonth()].substring(0, 3)}, {daysOfWeek[group.date.getDay()].substring(0, 3)}
                                        </span>
                                    </div>

                                    {/* Right Column: Events list */}
                                    <div className="flex-1 flex flex-col gap-1">
                                        {group.events.map((evt) => {
                                            const isDay = (evt.shift || 'Day').toLowerCase() === 'day';
                                            return (
                                                <div
                                                    key={evt.id}
                                                    onClick={(e) => handleEventClick(evt, e)}
                                                    className="group flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 p-2.5 -mx-2.5 rounded-xl hover:bg-slate-50 cursor-pointer transition-colors active:scale-[0.99]"
                                                >
                                                    {/* Shift dot + label */}
                                                    <div className="flex items-center gap-3 sm:w-32 flex-shrink-0">
                                                        <div className={`w-2.5 h-2.5 rounded-full shadow-sm flex-shrink-0 ${isDay ? 'bg-[#FCA311]' : 'bg-[#3F51B5]'}`} />
                                                        <span className="text-[13px] font-medium text-slate-600">{t((evt.shift || 'Day').toLowerCase() + 'Shift') || ((evt.shift || 'Day') + ' Shift')}</span>
                                                    </div>

                                                    {/* Client name */}
                                                    <span className="text-[14px] font-bold text-slate-800 flex-1 truncate">{evt.clientName || evt.title || 'Booking'}</span>

                                                    {/* Confirmed / Tentative badge */}
                                                    {evt.source === 'banquet' ? (
                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 flex-shrink-0">
                                                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
                                                            Confirmed
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200 flex-shrink-0">
                                                            <span className="w-1.5 h-1.5 rounded-full bg-amber-400 inline-block" />
                                                            Tentative
                                                        </span>
                                                    )}

                                                    {/* Venue */}
                                                    <div className="flex items-center gap-1.5 text-[12px] text-slate-400 group-hover:text-slate-600 transition-colors sm:text-right flex-shrink-0">
                                                        <span className="truncate max-w-[150px]">{VENUES.find(v => v.id === evt.venue)?.name || evt.venue}</span>
                                                    </div>
                                                </div>

                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                );
            case 'month':
            default:
                const blanks = Array(firstDayOfMonth).fill(null);
                const daysArray = Array.from({ length: daysInMonth }, (_, i) => i + 1);
                const totalSlots = [...blanks, ...daysArray];

                // Calculate number of rows needed for proper grid sizing
                const totalRows = Math.ceil(totalSlots.length / 7);

                return (
                    <div className="flex-1 flex flex-col overflow-hidden">
                        <div className="grid grid-cols-7 bg-slate-50/50 border-b border-slate-200">
                            {daysOfWeek.map((day, i) => (
                                <div key={`header-${i}`} className={`py-3 text-center text-[10px] font-black tracking-[0.2em] ${i === 0 || i === 6 ? 'text-rose-400' : 'text-slate-400'}`}>
                                    {day}
                                </div>
                            ))}
                        </div>
                        <div className="relative flex-1 min-h-0">
                            {/* Subtle loading overlay — keeps grid visible, just dims it */}
                            {banquetLoading && (
                                <div className="absolute inset-0 z-10 bg-white/50 flex items-center justify-center pointer-events-none">
                                    <div className="flex items-center gap-2 bg-white border border-slate-200 shadow-md rounded-full px-4 py-2">
                                        <RefreshCw className="w-3.5 h-3.5 text-blue-500 animate-spin" />
                                        <span className="text-xs font-semibold text-slate-600">Loading...</span>
                                    </div>
                                </div>
                            )}
                            <div
                                className={`h-full grid grid-cols-7 bg-slate-100 gap-[1px] overflow-y-auto min-h-0 custom-scrollbar transition-opacity duration-200 ${banquetLoading ? 'opacity-60' : 'opacity-100'}`}
                                style={{ gridTemplateRows: `repeat(${totalRows}, minmax(100px, 1fr))` }}
                            >
                                {totalSlots.map((day, index) => {
                                    const isToday = day &&
                                        currentDate.getMonth() === new Date().getMonth() &&
                                        currentDate.getFullYear() === new Date().getFullYear() &&
                                        day === new Date().getDate();

                                    const isWeekend = index % 7 === 0 || index % 7 === 6;

                                    const dayEvents = day ? bookedEvents.filter(e => {
                                        const eDate = new Date(e.eventDate);
                                        return eDate.getDate() === day &&
                                            eDate.getMonth() === currentDate.getMonth() &&
                                            eDate.getFullYear() === currentDate.getFullYear();
                                    }) : [];

                                    return (
                                        <CalendarCell
                                            key={`cell-${index}`}
                                            day={day}
                                            events={dayEvents}
                                            isToday={isToday}
                                            isWeekend={isWeekend}
                                            onEventClick={handleEventClick}
                                        />
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                );
        }
    };

    return (
        <div className="flex flex-col bg-slate-50/50 relative overflow-hidden max-h-[calc(100dvh-7rem)]">
            {/* Header / Toolbar */}
            <div className="mb-4 space-y-4 md:space-y-0 md:flex md:items-center md:justify-between px-2 md:px-0">
                <div className="flex items-center gap-3 animate-fade-in">
                    <div className="p-2.5 bg-blue-600 rounded-xl shadow-lg shadow-blue-200">
                        <CalendarIcon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                        <h1 className="text-xl md:text-2xl font-black text-slate-800 tracking-tight">
                            {t('bookingCalendar')}
                        </h1>
                    </div>
                    {/* Loading / error indicator */}
                    {banquetError && (
                        <button
                            onClick={() => fetchBanquetBookings(currentDate.getFullYear(), currentDate.getMonth())}
                            className="flex items-center gap-1.5 text-xs text-red-500 font-semibold hover:text-red-700 transition-colors"
                            title="Retry fetching banquet bookings"
                        >
                            <RefreshCw className="w-3.5 h-3.5" />
                            <span>Retry</span>
                        </button>
                    )}
                </div>

                <div className="flex flex-col sm:flex-row items-center gap-3">
                    {/* View Switcher (Google Calendar Style) */}
                    <div className="flex bg-slate-200/60 p-1 rounded-xl w-full sm:w-auto overflow-x-auto overflow-y-hidden custom-scrollbar border border-slate-200">
                        {[
                            { id: 'day', label: t('day') || 'Day' },
                            { id: 'week', label: t('week') || 'Week' },
                            { id: 'month', label: t('month') || 'Month' },
                            { id: 'year', label: t('year') || 'Year' },
                            { id: 'agenda', label: t('booking') || 'Booking' }
                        ].map(v => {
                            const isActive = view === v.id;
                            return (
                                <button
                                    key={v.id}
                                    onClick={() => setView(v.id)}
                                    className={`flex-1 sm:flex-none px-4 py-1.5 text-xs font-bold rounded-lg transition-all ${isActive
                                        ? 'bg-white text-blue-700 shadow-sm'
                                        : 'text-slate-500 hover:text-slate-800 hover:bg-white/50'
                                        }`}
                                >
                                    {v.label}
                                </button>
                            );
                        })}
                    </div>

                    <div className="flex items-center gap-2 bg-white p-1.5 rounded-xl border border-slate-200 shadow-sm w-full sm:w-auto">
                        <div className="relative flex-1 sm:flex-none">
                            <Dropdown className="w-auto">
                                <DropdownTrigger>
                                    <button className="w-full sm:w-[140px] text-xs font-bold text-slate-700 bg-slate-50 hover:bg-slate-100 rounded-lg py-1.5 px-3 flex justify-between items-center transition-colors outline-none h-8">
                                        <span className="truncate">
                                            {selectedVenue === 'all' ? (t('allVenues') || 'All Venues') : (venues.find(v => v.id === selectedVenue)?.name || selectedVenue)}
                                        </span>
                                        <ChevronDown className="w-3.5 h-3.5 text-slate-400 shrink-0 ml-1.5" />
                                    </button>
                                </DropdownTrigger>
                                <DropdownPopover className="w-[180px]">
                                    <DropdownMenu
                                        selectedKeys={new Set([selectedVenue])}
                                        selectionMode="single"
                                        onSelectionChange={(keys) => setSelectedVenue(Array.from(keys)[0])}
                                    >
                                        <DropdownItem id="all">{t('allVenues') || 'All Venues'}</DropdownItem>
                                        {venues.map(v => (
                                            <DropdownItem key={v.id} id={v.id}>{v.name}</DropdownItem>
                                        ))}
                                    </DropdownMenu>
                                </DropdownPopover>
                            </Dropdown>
                        </div>
                        <div className="relative flex-1 sm:flex-none">
                            <Dropdown className="w-auto">
                                <DropdownTrigger>
                                    <button className="w-full sm:w-[140px] text-xs font-bold text-slate-700 bg-slate-50 hover:bg-slate-100 rounded-lg py-1.5 px-3 flex justify-between items-center transition-colors outline-none h-8">
                                        <span className="truncate">
                                            {selectedShift === 'all' 
                                                ? (t('allShifts') || 'All Shifts') 
                                                : selectedShift === 'day' 
                                                    ? (t('dayShift') || 'Morning Shift') 
                                                    : (t('nightShift') || 'Evening Shift')}
                                        </span>
                                        <ChevronDown className="w-3.5 h-3.5 text-slate-400 shrink-0 ml-1.5" />
                                    </button>
                                </DropdownTrigger>
                                <DropdownPopover className="w-[180px]">
                                    <DropdownMenu
                                        selectedKeys={new Set([selectedShift])}
                                        selectionMode="single"
                                        onSelectionChange={(keys) => setSelectedShift(Array.from(keys)[0])}
                                    >
                                        <DropdownItem id="all">{t('allShifts') || 'All Shifts'}</DropdownItem>
                                        <DropdownItem id="day">{t('dayShift') || 'Morning Shift'}</DropdownItem>
                                        <DropdownItem id="night">{t('nightShift') || 'Evening Shift'}</DropdownItem>
                                    </DropdownMenu>
                                </DropdownPopover>
                            </Dropdown>
                        </div>
                    </div>
                </div>
            </div>



            {/* Calendar Container */}
            <div className="flex-1 bg-white border border-slate-200 shadow-xl rounded-2xl md:rounded-[2rem] overflow-hidden flex flex-col animate-bounce-in">

                {/* Month/Year Nav block (hide in Agenda sometimes, or just show) */}
                <div className="flex flex-col sm:flex-row items-center justify-between px-4 py-4 md:px-8 border-b border-slate-100 bg-white z-10 gap-4">
                    <div className="flex items-center gap-4">
                        <h2 className="text-xl md:text-2xl font-extrabold text-slate-800 min-w-[180px] md:min-w-[220px]">
                            {view !== 'year' && `${monthNames[currentDate.getMonth()]} `}
                            {currentDate.getFullYear()}
                        </h2>
                        <button
                            onClick={goToday}
                            className="hidden md:block px-4 py-1.5 text-sm font-bold text-slate-700 bg-slate-50 hover:bg-slate-100 rounded-lg shadow-sm border border-slate-200 transition-all active:scale-95"
                        >
                            Today
                        </button>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={goToday} className="md:hidden px-3 py-1 text-xs font-bold text-slate-700 bg-slate-50 rounded shadow-sm border border-slate-200 mr-2">Today</button>
                        <button onClick={() => navigateDate(-1)} className="p-2.5 text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all active:scale-90 border border-slate-100">
                            <ChevronLeft className="w-5 h-5" />
                        </button>
                        <button onClick={() => navigateDate(1)} className="p-2.5 text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all active:scale-90 border border-slate-100">
                            <ChevronRight className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Render Selected View */}
                {renderContent()}

            </div>

            {/* Modals & Popovers */}
            {popupEvent && (
                <BookingDetailModal
                    event={popupEvent.event}
                    onClose={() => setPopupEvent(null)}
                    onViewLead={handleViewLead}
                    t={t}
                    currentUser={currentUser}
                    leads={leads}
                />
            )}

            {financeLeadForPayment && (
                <PaymentDetailModal
                    lead={financeLeadForPayment}
                    onClose={() => setFinanceLeadForPayment(null)}
                    onUpdate={() => setFinanceLeadForPayment(null)} // Or refresh data
                />
            )}
        </div>
    );
}
